<template>
  <IonApp id="ionApp">
    <IonRouterOutlet />
  </IonApp>

  <SerialKeyboard></SerialKeyboard>
</template>

<script setup lang="ts">
import { IonApp, IonRouterOutlet, isPlatform, modalController } from '@ionic/vue';
import { onMounted, onUnmounted } from 'vue';
import { useCameraPermission } from './stores/permissions/camera-permission.store';
import { useLocationPermission } from './stores/permissions/location-permission.store';
import { App } from '@capacitor/app';
import { useBlePermission } from './stores/permissions/ble-permission.store';
import { usePhotoGalleryPermission } from './stores/permissions/gallery-permission.store';
import SerialKeyboard from './components/forms/serial-input/SerialKeyboard.vue';
import { useAppInfo } from './stores/app-info.store';
import ResetPasswordModal from './components/user-account/ResetPasswordModal.vue';
import { UserService } from './http-services/user.service';
import { useToast } from './composables/use-toast';
import { StatusBar, Style } from '@capacitor/status-bar';
import { logger } from '@sentry/vue';

let listener: { remove: () => void };
let deepLinkListener: { remove: () => void };
const locationPermissions = useLocationPermission();
const cameraPermissions = useCameraPermission();
const blePermissions = useBlePermission();
const photoPermissions = usePhotoGalleryPermission();
const appInfo = useAppInfo();

async function openModal(token: string) {
  const modal = await modalController.create({
    component: ResetPasswordModal,
    componentProps: {
      token,
    },
  });
  await modal.present();
}
const userService = new UserService();
const toast = useToast();

async function StyleStatusBar() {
  try {
    await StatusBar.setStyle({ style: Style.Light });
  } catch (e) {
    logger.info('Failed to set status bar to Light', { error: e });
  }
}

onMounted(async () => {
  await StyleStatusBar();
  await locationPermissions.onAppResume();
  await blePermissions.onAppResume();
  await cameraPermissions.onAppResume();
  await photoPermissions.onAppResume();
  listener = await App.addListener('appStateChange', async (state) => {
    console.log('APP STATE', state);
    if (state.isActive) {
      await StyleStatusBar();
      await locationPermissions.onAppResume();
      await blePermissions.onAppResume();
      await cameraPermissions.onAppResume();
      await photoPermissions.onAppResume();
      appInfo.onAppResume();
    }
  });
  deepLinkListener = await App.addListener('appUrlOpen', async (data) => {
    const url = new URL(data.url);
    if (url.pathname === '/resetPassword') {
      openModal(url.searchParams.get('resetPasswordToken') || '');
    } else if (url.pathname === '/activateAccount') {
      const res: any = await userService.activateAccount(url.searchParams.get('activateAccountToken') || '');
      if (res.success) {
        toast.successToast(res.message);
      } else {
        toast.errorToast(res.message);
      }
    }
  });
  if (import.meta.env.DEV) {
    //@ts-ignore
    window.openResetPasswordModal! = () => {
      openModal('');
    };
  }
  await appInfo.checkVersion();
});

onUnmounted(() => {
  listener?.remove();
  deepLinkListener?.remove();
  appInfo.stopLiveUpdateCheckInterval();
});
</script>

<style scoped></style>
